import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Caption1, Text, makeStyles } from '@fluentui/react-components';
import { ShellIcons, StatusDot, complianceTokenFor, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { OfferTracker, offerTrackerSteps } from '@/features/offer-stage';
import type { OfferRecord } from '@vigeo/shared';

/**
 * The offer card chrome shared by frames 06b `295:801` and `297:932`.
 *
 * Both draw one bordered card: an intro (section crumb, title, one line of explanation), the
 * body, then a footer band on a quieter fill with the actions at its right.
 */

/* ---- Where an offer stands ---------------------------------------------------------------- */

/**
 * THE LABEL DECISION (#261/#262).
 *
 * The stored enum is `draft | reviewed | issued | withdrawn`; the Offers list groups the reviewed
 * state under "Ready to send" (frame `293:444`, shipped in #295) and the record's own tracker calls
 * it "Reviewed" (frames `295:801` and `297:932`). Those are not two names for one thing, and frame
 * `297:932` settles it in as many words: its badge reads "Reviewed · ready to send".
 *
 * So: **"Reviewed" is the name of the state**, used wherever the state itself is named — the
 * tracker, the badge's stem, the record's sentences. "Ready to send" stays the Offers list's GROUP
 * heading, which answers a different question — what is waiting for you to do — and is what an
 * approved frame draws there. Nothing renames `offerGroups`; the list keeps its wording.
 *
 * The four words themselves are `offerTrackerSteps`, shared with rows 2.1/2.2 rather than restated.
 */
export function reachedStage(status: OfferRecord['status'], signed: boolean): number {
  if (signed) return offerTrackerSteps.indexOf('Signed');
  if (status === 'issued') return offerTrackerSteps.indexOf('Sent');
  if (status === 'reviewed') return offerTrackerSteps.indexOf('Reviewed');
  return offerTrackerSteps.indexOf('Draft');
}

/* ---- The card ---------------------------------------------------------------------------- */

const useStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', alignItems: 'stretch', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, overflow: 'hidden',
  },
  intro: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`, minWidth: 0,
  },
  crumb: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200,
  },
  crumbLink: {
    color: tokens.colorBrandForeground1, textDecorationLine: 'none',
    display: 'inline-flex', alignItems: 'center',
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  crumbHere: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  title: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  lede: { color: tokens.colorNeutralForeground2 },
  trackerWrap: { padding: `0 ${tokens.spacingHorizontalXL} ${tokens.spacingVerticalL}`, minWidth: 0 },
  footer: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  footerNote: { flexGrow: 1, flexBasis: '16rem', minWidth: 0, color: tokens.colorNeutralForeground2 },
  row: {
    display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: tokens.spacingHorizontalXXL,
    padding: tokens.spacingHorizontalXL, minWidth: 0, flexWrap: 'wrap',
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  rowLabel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, width: '12.5rem', flexShrink: 0, minWidth: 0 },
  rowLabelName: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
  rowFields: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, flexGrow: 1, flexBasis: '20rem', minWidth: 0 },
  values: { display: 'flex', flexDirection: 'row', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', minWidth: 0 },
  value: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexGrow: 1, flexBasis: 0, minWidth: '10rem' },
  valueName: { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  valueText: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, whiteSpace: 'pre-line', overflowWrap: 'anywhere' },
});

export function OfferCard({ crumb, title, lede, reached, children, footer }: {
  /** The section this step belongs to, and where it goes back to. */
  readonly crumb: { readonly label: string; readonly href: string; readonly here: string; readonly onClick?: () => void };
  readonly title: string;
  readonly lede: string;
  /** Drawn under the intro, as frame `295:801` does. Omitted where the row puts it elsewhere. */
  readonly reached?: number;
  readonly children: ReactNode;
  readonly footer: ReactNode;
}) {
  const styles = useStyles();
  return (
    <section className={styles.card} aria-labelledby="offer-step-title">
      <div className={styles.intro}>
        <span className={styles.crumb}>
          <Link to={crumb.href} className={styles.crumbLink} onClick={crumb.onClick === undefined ? undefined : (event) => { event.preventDefault(); crumb.onClick?.(); }}>{crumb.label}</Link>
          <ShellIcons.chevronRight />
          <span className={styles.crumbHere}>{crumb.here}</span>
        </span>
        <h2 id="offer-step-title" className={styles.title}>{title}</h2>
        <Text className={styles.lede}>{lede}</Text>
      </div>
      {reached === undefined ? null : <div className={styles.trackerWrap}><OfferTracker steps={offerTrackerSteps} reached={reached} label="Offer status" /></div>}
      {children}
      <div className={styles.footer}>{footer}</div>
    </section>
  );
}

/** Frame `297:932`'s two-column row: a 200px label column, then the fields. */
export function OfferRow({ label, hint, children }: {
  readonly label: string;
  readonly hint?: string;
  readonly children: ReactNode;
}) {
  const styles = useStyles();
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>
        <span className={styles.rowLabelName}>{label}</span>
        {hint === undefined ? null : <Caption1>{hint}</Caption1>}
      </div>
      <div className={styles.rowFields}>{children}</div>
    </div>
  );
}

/** A line of label-over-value facts, as both frames draw them. */
export function OfferValues({ items }: { readonly items: readonly { readonly label: string; readonly value: string }[] }) {
  const styles = useStyles();
  return (
    <div className={styles.values}>
      {items.map((item) => (
        <div key={item.label} className={styles.value}>
          <span className={styles.valueName}>{item.label}</span>
          <span className={styles.valueText}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

/** "$25.00 per visit", from the offer's own compensation rows. Both frames write rates this way. */
export function rateText(row: OfferRecord['terms']['compensation'][number]): string {
  return `$${row.amount.toFixed(2)} per ${row.unit}`;
}

/** The badge frame `297:932` puts above the tracker. See `reachedStage` for why it reads this way. */
export function OfferStageBadge({ reached, past }: { readonly reached: number; readonly past: boolean }) {
  // The existing "Past the accept by date" wording, reused rather than restated. No stored status
  // says a deadline has gone by (see the Offers list), and none is added here.
  // Frames 1.4 (297:932) and 2.1 (321:1066) draw this as a dot and plain text, like every other
  // state. It was missed in the first StatusDot pass, which converted five sites and not this one.
  if (past) return <StatusDot color={complianceTokenFor.warning}>Past the accept by date</StatusDot>;
  if (offerTrackerSteps[reached] === 'Reviewed') return <StatusDot color={complianceTokenFor.good}>Reviewed · ready to send</StatusDot>;
  return <StatusDot color={tokens.colorNeutralForeground3}>{offerTrackerSteps[reached]}</StatusDot>;
}
